<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0 bg-emerald-500">
      <div class="flex flex-wrap items-center justify-between">
        <h3 class="font-semibold text-lg text-white">Vendors List</h3>
        <div class="flex gap-2">
          <button
            @click="inviteVendor"
            class="bg-white text-blue-600 text-sm font-bold px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fas fa-envelope mr-1"></i> Invite Vendor
          </button>
          <button
            @click="openModal"
            class="bg-white text-emerald-600 text-sm font-bold px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fas fa-plus mr-1"></i> Add Vendor
          </button>
        </div>
      </div>
    </div>

    <div class="block w-full overflow-x-auto">
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Trade Name</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Vendor ID</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Name</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-left text-xs font-medium uppercase tracking-wider">Email</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-center text-xs font-medium uppercase tracking-wider">Address</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-center text-xs font-medium uppercase tracking-wider">Status</th>
            <th class="text-white px-6 py-3 bg-blueGray-600 text-center text-xs font-medium uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in enrolledAccounts" :key="account.id">
            <td class="px-6 py-4 text-sm">{{ account.vendor.company_name }}</td>
            <td class="px-6 py-4 text-sm">
              <span v-if="account.vendor.vendor_code" class="font-mono font-bold bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">{{ account.vendor.vendor_code }}</span>
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-6 py-4 text-sm">
              {{ account.vendor.first_name }} {{ account.vendor.last_name }}
            </td>
            <td class="px-6 py-4 text-sm">{{ account.vendor.email }}</td>
            <td class="px-6 py-4 text-center text-sm">
              {{ account.vendor.address_1 }} {{ account.vendor.address_2 }},
              {{ account.vendor.city }}, {{ account.vendor.state }}
              {{ account.vendor.zipcode }}
            </td>
            <td class="px-6 py-4 text-center text-sm">
              <span
                :class="{
                  'text-emerald-600 font-bold': account.status === 'Active',
                  'text-red-500 font-bold': account.status === 'Inactive',
                }"
              >{{ account.status }}</span>
            </td>
            <td class="px-6 py-4 text-center text-sm">
              <button
                v-if="account.status === 'Active'"
                @click="terminate_enrollment(account.id)"
                class="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded hover:bg-red-600"
              >
                Terminate
              </button>
            </td>
          </tr>
          <tr v-if="enrolledAccounts.length === 0">
            <td colspan="6" class="px-6 py-8 text-center text-gray-400">
              No enrolled vendors yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <hire-vendor-modal
      :show="showModal"
      :vendors="vendorsList"
      @close="closeModal"
      @submit="hireVendor"
    />
  </div>
</template>

<script>
import HireVendorModal from "@/components/Modal/HireVendorModal.vue";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  components: { HireVendorModal },
  data() {
    return {
      showModal: false,
      vendorsList: [],
      enrolledAccounts: [],
    };
  },
  mounted() {
    this.fetch_enrolled();
    this.fetch_vendors();
  },
  methods: {
    openGmailCompose(email, signupUrl) {
      const subject = "Vendor sign-up link | BFMS";
      const body = [
        "Hello,",
        "",
        "Please use this vendor sign-up link:",
        signupUrl,
        "",
        "Blessed FMS",
      ].join("\n");

      const gmailUrl =
        `https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1` +
        `&to=${encodeURIComponent(email)}` +
        `&su=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      window.open(gmailUrl, "_blank", "noopener,noreferrer");
    },

    async inviteVendor() {
      const { value: email } = await Swal.fire({
        title: "Invite Vendor",
        text: "Enter the vendor's email address. They will receive a vendor sign-up link.",
        input: "email",
        inputPlaceholder: "vendor@example.com",
        showCancelButton: true,
        confirmButtonText: "Send Invite",
        confirmButtonColor: "#3b82f6",
        inputValidator: (value) => {
          if (!value) return "Please enter an email address.";
        },
      });

      if (!email) return;

      Swal.fire({
        title: "Sending invitation...",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const token = localStorage.getItem("token");

        const res = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/invite_vendor`,
          {
            email: email,
            token: token,
          }
        );

        Swal.close();

        if (res.data.signup_url && res.data.email_sent === false) {
          this.openGmailCompose(email, res.data.signup_url);

          Swal.fire({
            icon: "success",
            title: "Gmail opened",
            html: `
              <p class="text-left text-sm mb-2">
                The vendor invitation was created, but the email server could not send it. A Gmail compose tab was opened with the vendor sign-up link.
              </p>
             
            `,
            confirmButtonText: "OK",
          });
          return;
        }

        if (res.data.email_sent === true) {
          Swal.fire({
            icon: "success",
            title: "Invitation sent!",
            text: res.data.message || `Vendor sign-up link sent to ${email}`,
          });
          return;
        }

        if (res.data.signup_url) {
          this.openGmailCompose(email, res.data.signup_url);
        }

        Swal.fire({
          icon: "warning",
          title: "Invitation created",
          text: res.data.message || "The vendor invitation was created.",
        });
      } catch (error) {
        console.error("Invite vendor error:", error);

        let msg = "Network error. Please try again.";

        if (error.response && error.response.data) {
          msg =
            error.response.data.error ||
            error.response.data.message ||
            msg;
        }

        Swal.fire({
          icon: "error",
          title: "Failed to send vendor invitation",
          text: msg,
        });
      }
    },

    openModal() {
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async terminate_enrollment(enrollment_id) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to terminate this enrollment?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, terminate it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Terminating enrollment...",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          const res = await axios.post(
            `${process.env.VUE_APP_BACKEND_DOMAIN}/api/terminate_enrollment`,
            { enrollment_id },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            }
          );

          Swal.close();

          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.data.message || "Enrollment terminated successfully!",
          });

          this.fetch_enrolled();
          this.fetch_vendors();
        } catch (error) {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              error.response?.data?.message || "Failed to terminate enrollment",
          });
          console.error("Terminate enrollment failed", error);
        }
      }
    },

    async hireVendor(formData) {
      this.showModal = false;
      Swal.fire({
        title: "Uploading...",
        text: "Please wait.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/hire-vendor`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.fetch_enrolled();
        this.fetch_vendors();
        Swal.fire("Success", "Enrollment Successful", "success");
      } catch (error) {
        console.error("Hire vendor failed", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to add vendor.",
        });
      }
    },

    async fetch_vendors() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-all-vendor`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        this.vendorsList = res.data.data;
      } catch (error) {
        console.error("Fetch vendors failed", error);
      }
    },

    async fetch_enrolled() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-vendor-enrolled-accounts`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        this.enrolledAccounts = res.data.Accounts;
      } catch (error) {
        console.error("Fetch enrolled vendors failed", error);
      }
    },
  },
};
</script>
